<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="form_wrapper">
            <div class="ls-flow--title">Confirm Scope Change</div>

            <form novalidate class="ls-flow--form" @submit.prevent="createScheduleScopeChange">
                <div>
                    <p>Next service scheduled on</p>
                    <p class="ls-flow--date-range">{{ range }}</p>
                </div>

                <LsRadios
                    v-model="scope"
                    track-id="change_scope_view-options"
                    name="scope"
                    label="New scope"
                    :options="options"
                />

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="change_scope_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="change_scope_view-confirm_scope_change"
                    text="Confirm Scope Change"
                    :loading="isUpdating"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsFilters, lsModalService } from 'ls-vue-common';
import gql from 'graphql-tag';

import { lsHelpers } from '@/helpers';
import ScheduleScopeChange from '@/graphql/queries/ScheduleScopeChange.gql';
import CreateScheduleScopeChange from '@/graphql/mutations/CreateScheduleScopeChange.gql';
import Schedule from '@/graphql/queries/Schedule.gql';
import { LsButton, LsRadios, LsInput } from '@/components/common';
import { FlowSuccessModal } from '@/modals';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import { apolloClient } from '@/vue-apollo';
import LsError from '@/components/common/LsError/LsError.vue';

export default {
    name: 'ChangeScopeView',
    components: {
        LsButton,
        LsRadios,
        LsInput,
        LsTemplatedLoading,
        LsError,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,
            isInvalid: false,

            options: [],
            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                    }
                `,
            }),
            schedule: apolloClient.readFragment({
                id: `Schedule:${this.$route.params.schedule_id}`,
                fragment: gql`
                    fragment schedule on Schedule {
                        next_customer_starts_at
                        next_customer_ends_at
                    }
                `,
            }),

            // form data
            scope: null,
            zendesk_ticket_id: '',
        };
    },
    apollo: {
        scheduleScopeChange: {
            query: ScheduleScopeChange,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                    schedule_id: _.toNumber(this.$route.params.schedule_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    const dictionary = {
                        scope_full: 'Full property',
                        scope_front: 'Front-yard',
                        scope_back: 'Backyard',
                    };

                    this.options = _.map(data.scheduleScopeChange, (option) => {
                        return {
                            value: option.scope,
                            label: `${dictionary[option.scope]} only - ${lsFilters.currency({
                                amount: option.total,
                            })}`,
                            subLabel: lsHelpers.pricingSubLabel(option),
                        };
                    });

                    if (this.options.length) {
                        this.scope = this.options[0].value;
                    }
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.scheduleScopeChange.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
            // "no-cache" would be better here - but the loading state was being wonky when I tried that
            fetchPolicy: 'network-only',
        },
    },
    computed: {
        range() {
            return lsHelpers.dateRange({
                start: this.schedule.next_customer_starts_at,
                end: this.schedule.next_customer_ends_at,
            });
        },
    },
    methods: {
        async createScheduleScopeChange() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateScheduleScopeChange,
                    variables: {
                        customer_id: _.toNumber(this.$route.params.customer_id),
                        property_id: _.toNumber(this.$route.params.property_id),
                        schedule_id: _.toNumber(this.$route.params.schedule_id),
                        scope_full: this.scope === 'scope_full',
                        scope_front: this.scope === 'scope_front',
                        scope_back: this.scope === 'scope_back',
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                    refetchQueries: [
                        {
                            query: Schedule,
                            variables: {
                                customer_id: _.toNumber(this.$route.params.customer_id),
                                property_id: _.toNumber(this.$route.params.property_id),
                                schedule_id: _.toNumber(this.$route.params.schedule_id),
                            },
                        },
                    ],
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Service scope changed for ${this.property.street}`,
                            copy: data.createScheduleScopeChange.copy,
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>
